import { CartItem } from '@pedix-workspace/utils';
import { StockInfo } from './stock-info.model';
import { StockDelta } from './stock-delta.model';
import { ProductStockRecord } from './stock.types';

export type StockInfoMap = Record<string, StockInfo>;

export const isEmbeddedOptionItemStock = (record: ProductStockRecord): boolean =>
  record.entityType === 'option-item' && record.productId !== record.id;

export class StockMap {
  private stockInfoMap: Record<string, StockInfo>;

  constructor(
    private productStock: ProductStockRecord[],
    private cartItems: CartItem[],
    private originalCartItems?: CartItem[],
    private stockCheckDisabled: boolean = false,
  ) {
    this.stockInfoMap = this.processStockInfoMap(
      this.productStock,
      this.cartItems,
      this.originalCartItems,
      this.stockCheckDisabled,
    );
  }

  getStockInfoByItem(itemId: string): StockInfo {
    return (
      this.stockInfoMap[itemId] ||
      new StockInfo({
        item: undefined, // sin record en `productStock` == stock infinito
        stockCheckDisabled: this.stockCheckDisabled,
      })
    );
  }

  getStockInfoForCartItems(): StockInfo[] {
    return Object.values(this.stockInfoMap).filter(stockInfo => stockInfo.totalStockInCart > 0);
  }

  getStockInfoDeltas(): StockDelta[] {
    const stockDeltas: StockDelta[] = [];

    Object.values(this.stockInfoMap).forEach(stockInfo => {
      if (stockInfo.hasUnlimitedStock) {
        return;
      }
      if (isEmbeddedOptionItemStock(stockInfo.item!)) {
        return;
      }
      if (stockInfo.totalStockInCart > 0) {
        const stockDifference = stockInfo.totalStockInCart - stockInfo.reservedStockForOrder;

        stockDeltas.push({
          item: stockInfo.item!,
          delta: stockDifference === 0 ? 0 : stockDifference * -1,
        });
      } else if (stockInfo.reservedStockForOrder > 0) {
        stockDeltas.push({
          item: stockInfo.item!,
          delta: stockInfo.reservedStockForOrder,
        });
      }
    });

    return stockDeltas;
  }

  getStockInfoArrayByCartItem(cartItemId: string): StockInfo[] {
    return this.getStockInfoForCartItems().filter(
      stockInfo => stockInfo.getConsumedStock(cartItemId) > 0,
    );
  }

  getItemsInCartWithStockErrors(): StockInfo[] {
    return this.getStockInfoForCartItems().filter(stockInfo => stockInfo.hasMissingStock);
  }

  /**
   * Infinito = entidad sin record O record con `stockQty: null` (sin control). Las fronteras (API front +
   * transacción de venta) descartan los `null` por eficiencia, pero `StockInfo.hasUnlimitedStock` los
   * trata como infinito igual (defensivo ante caminos que leen `productStock` directo, p.ej. el
   * order-editor del admin). Se siembra una entrada por record; el consumo/reserva del carrito solo se
   * aplica a entradas existentes: las entidades sin record son infinitas y no requieren tracking.
   */
  private processStockInfoMap(
    productStock: ProductStockRecord[],
    cartItems: CartItem[],
    originalCartItems?: CartItem[],
    stockCheckDisabled: boolean = false,
  ) {
    const stockInfoMap: StockInfoMap = {};

    // 1. Siembra stock base desde los records finitos de `productStock`
    productStock.forEach(record => {
      stockInfoMap[record.id] = new StockInfo({
        item: record,
        stockCheckDisabled,
      });
    });

    // 2. Suma stock "reservado" desde originalCartItems (solo entidades finitas)
    if (originalCartItems) {
      originalCartItems.forEach(cartItem => {
        if (cartItem.selectedPresentation) {
          const displayItemQuantities = cartItem.selectedPresentation.option?.displayItemQuantities;

          cartItem.selectedPresentation.items.forEach(presentation => {
            if (stockInfoMap[presentation.id]) {
              const consumedStock = displayItemQuantities
                ? presentation.quantity || 1
                : cartItem.qty;

              stockInfoMap[presentation.id].setReservedStock(consumedStock);
            }
          });
        } else {
          if (stockInfoMap[cartItem.product.id]) {
            stockInfoMap[cartItem.product.id].setReservedStock(cartItem.qty);
          }
        }
        if (cartItem.selectedOptions) {
          cartItem.selectedOptions.forEach(selectedOption => {
            selectedOption.items.forEach(item => {
              if (stockInfoMap[item.id]) {
                stockInfoMap[item.id].setReservedStock((item.quantity || 1) * cartItem.qty);
              }
            });
          });
        }
      });
    }

    // 3. Descuenta stock consumido por cartItems (solo entidades finitas)
    cartItems.forEach(cartItem => {
      if (cartItem.selectedPresentation) {
        const displayItemQuantities = cartItem.selectedPresentation.option?.displayItemQuantities;

        cartItem.selectedPresentation.items.forEach(presentation => {
          if (stockInfoMap[presentation.id]) {
            const consumedStock = displayItemQuantities ? presentation.quantity || 1 : cartItem.qty;

            stockInfoMap[presentation.id].setConsumedStock(consumedStock, cartItem.id);
          }
        });
      } else {
        if (stockInfoMap[cartItem.product.id]) {
          stockInfoMap[cartItem.product.id].setConsumedStock(cartItem.qty, cartItem.id);
        }
      }
      if (cartItem.selectedOptions) {
        cartItem.selectedOptions.forEach(selectedOption => {
          selectedOption.items.forEach(item => {
            if (stockInfoMap[item.id]) {
              stockInfoMap[item.id].setConsumedStock(
                (item.quantity || 1) * cartItem.qty,
                cartItem.id,
              );
            }
          });
        });
      }
    });

    return stockInfoMap;
  }
}
